/**
 * Which setup email a person gets next, if any.
 *
 * Pure and client-safe on purpose: no database, no clock, no environment
 * except an optional base URL the caller may override. Everything the decision
 * needs arrives in the input, so the whole thing is unit-testable and the
 * daily job stays a thin loop around it.
 *
 * The shape of the idea: the property's setup workflow already knows what is
 * finished and what is not, and the product renders that same evaluation in
 * the panel. The email sequence is the same five steps, read once a day, with
 * one extra question layered on top — was this step finished before this
 * person arrived? A step somebody else finished is worth a short tour of what
 * it does. A step nobody has finished is worth asking for, but only from the
 * people whose job it is to set things up, and only while the organization has
 * nothing tracking yet.
 *
 * Everything the sequence needs to remember lives in `User.onboardingSequence`
 * (`OnboardingSequenceState`), snapshotted on the first evaluation.
 */

import type { SubjectType } from "../types/property";
import { subjectModeConfig } from "../utils/subject-mode";
import { getWorkflow } from "../workflows/catalog";
import { isSetupEligible, setupWorkflowIdFor } from "../workflows/setup";
import type { EvaluatedWorkflow } from "../workflows/types";

// ── State ──────────────────────────────────────────────────────────────────────

/**
 * Structural copy of `OnboardingSequenceState` in
 * `packages/database/src/types/onboarding-sequence.ts`. Spelled out rather
 * than imported so this module stays reachable from `@repo/core/client`;
 * `setup-sequence.test.ts` asserts the two stay assignable both ways.
 */
export interface OnboardingSequenceState {
	organizationId: string;
	propertyId: string;
	startedAt: string;
	tourEligible: boolean;
	doneAtStart: string[];
	toursSent: string[];
	sentCount: number;
	closingSentAt?: string;
}

export type SetupEmailVariant = "todo" | "tour" | "closing";

export interface SetupEmailPlan {
	/** W2 step id, or `closing` for the one wrap-up email. */
	stepId: string;
	variant: SetupEmailVariant;
	/** The property's active setup run, when there is one. */
	runId: string | null;
	href: string;
	/** `propertyId:stepId:variant` — stored as `lastOnboardingEmailKey`. */
	key: string;
}

export interface SetupSequenceUser {
	id: string;
	email: string;
	emailVerified: boolean;
	onboardingEmailsUnsubscribed: boolean;
	/** Platform role. `admin` is Spyglasses staff, never in the sequence. */
	role: string | null;
	/** The person's seat in the organization the sequence is about. */
	membership: { role: string; createdAt: Date };
}

export interface SetupSequenceOrganization {
	id: string;
	slug: string | null;
	/** Any property in the organization tracking with a goal attached. */
	hasTrackingReadyProperty: boolean;
	/** The public-report organization gets no lifecycle email. */
	useForAiVisibilityReports?: boolean;
}

export interface SetupSequenceProperty {
	id: string;
	createdAt: Date;
	subjectType: string | null;
	isProspect?: boolean | null;
}

export interface PlanSetupEmailInput {
	user: SetupSequenceUser;
	organization: SetupSequenceOrganization;
	property: SetupSequenceProperty;
	/** W2 evaluated over the property's state and its active setup run. */
	evaluation: EvaluatedWorkflow;
	/** The property's active setup run, when there is one. */
	setupRunId: string | null;
	/** What is already remembered about this person. Null before the first look. */
	sequence: OnboardingSequenceState | null;
	lastKey: string | null;
	lastSentAt: Date | null;
	now: Date;
	/** Defaults to `NEXT_PUBLIC_SITE_URL`. */
	baseUrl?: string;
}

// ── Constants ──────────────────────────────────────────────────────────────────

const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;

/** One email a day at most, whatever else changed. */
const MIN_GAP_MS = 24 * HOUR_MS;
/** The same ask twice in a row waits this long. */
const REPEAT_GAP_MS = 3 * DAY_MS;
/** Nobody hears from the sequence more than this many times. */
const MAX_EMAILS = 8;

/** Seats whose job it is to set the property up. */
const SETUP_ROLES = new Set(["owner", "admin"]);

const INTERNAL_EMAIL_DOMAIN = "@spyglasses.io";

const CLOSING_STEP_ID = "closing";

/**
 * Where the closing email lands. The visibility drill-down is about the
 * subject's own share of voice, so a subject with no focal brand (a category)
 * goes to the Brands ranking instead; the visibility page would bounce it home.
 */
function closingRouteFor(subjectType: string | null | undefined): string {
	return subjectModeConfig(subjectType as SubjectType | null | undefined)
		.hasFocalSubject
		? "/dashboard/visibility"
		: "/dashboard/competitors";
}

// ── Helpers ────────────────────────────────────────────────────────────────────

/** better-auth stores platform roles as a comma-separated string. */
function hasPlatformRole(role: string | null | undefined, want: string) {
	return (role ?? "")
		.split(",")
		.map((part) => part.trim().toLowerCase())
		.includes(want);
}

function isSetupSeat(membershipRole: string): boolean {
	return membershipRole
		.split(",")
		.map((part) => part.trim().toLowerCase())
		.some((part) => SETUP_ROLES.has(part));
}

function siteBase(explicit?: string): string {
	return (
		explicit ??
		process.env.NEXT_PUBLIC_SITE_URL ??
		"https://www.spyglasses.io"
	).replace(/\/+$/, "");
}

function withRun(url: string, runId: string | null): string {
	if (!runId) {
		return url;
	}
	return `${url}${url.includes("?") ? "&" : "?"}workflowRun=${encodeURIComponent(runId)}`;
}

/**
 * Which page a step sends people to.
 *
 * Read from the checklist the property is actually on: a market is on W32,
 * whose step ids are not W2's, so looking the id up in the wrong definition
 * would drop every step and send nothing.
 */
function routeFor(
	stepId: string,
	subjectType: string | null | undefined,
): string | null {
	const def = getWorkflow(setupWorkflowIdFor(subjectType));
	return def?.steps.find((step) => step.id === stepId)?.route ?? null;
}

/**
 * Is this person in the sequence at all?
 *
 * Deliberately separate from the step walk: the job asks the same question to
 * decide whether to remember anything about them.
 */
export function isSequenceEligible(
	input: Pick<PlanSetupEmailInput, "user" | "organization" | "property">,
): boolean {
	const { user, organization, property } = input;
	if (!user.emailVerified || user.onboardingEmailsUnsubscribed) {
		return false;
	}
	if (hasPlatformRole(user.role, "admin")) {
		return false;
	}
	if (user.email.trim().toLowerCase().endsWith(INTERNAL_EMAIL_DOMAIN)) {
		return false;
	}
	if (organization.useForAiVisibilityReports) {
		return false;
	}
	return isSetupEligible(property);
}

/**
 * The sequence record as it should be stored after this evaluation.
 *
 * The first look snapshots two things that can never be recovered later: was
 * the property here before this person, and which steps were already finished
 * when they arrived. Everything after that leaves the snapshot alone.
 */
export function resolveSequenceState(
	input: Pick<
		PlanSetupEmailInput,
		"user" | "organization" | "property" | "evaluation" | "sequence" | "now"
	>,
): OnboardingSequenceState | null {
	if (!isSequenceEligible(input)) {
		return null;
	}
	const existing = input.sequence;
	if (existing && existing.propertyId === input.property.id) {
		return existing;
	}
	return {
		organizationId: input.organization.id,
		propertyId: input.property.id,
		startedAt: input.now.toISOString(),
		tourEligible:
			input.property.createdAt.getTime() <
			input.user.membership.createdAt.getTime(),
		doneAtStart: input.evaluation.steps
			.filter((step) => !step.hidden && step.status === "done")
			.map((step) => step.id),
		toursSent: [],
		sentCount: 0,
	};
}

/**
 * The sequence record after an email has actually gone out.
 *
 * A tour is one-shot: nothing about a finished step changes tomorrow, so
 * without a record of what has been explained the walk would hand out the
 * same first tour every morning. An unfinished step is different — it is
 * still unfinished, and the cadence rules decide when to ask again.
 */
export function advanceSequenceState(
	sequence: OnboardingSequenceState,
	plan: SetupEmailPlan,
	now: Date,
): OnboardingSequenceState {
	return {
		...sequence,
		sentCount: sequence.sentCount + 1,
		toursSent:
			plan.variant === "tour" && !sequence.toursSent.includes(plan.stepId)
				? [...sequence.toursSent, plan.stepId]
				: sequence.toursSent,
		...(plan.variant === "closing"
			? { closingSentAt: now.toISOString() }
			: {}),
	};
}

// ── Planner ────────────────────────────────────────────────────────────────────

export function planSetupEmail(
	input: PlanSetupEmailInput,
): SetupEmailPlan | null {
	const sequence = resolveSequenceState(input);
	if (!sequence) {
		return null;
	}

	if (sequence.sentCount >= MAX_EMAILS) {
		return null;
	}
	// One a day, whoever they are and whatever moved.
	if (
		input.lastSentAt &&
		input.now.getTime() - input.lastSentAt.getTime() < MIN_GAP_MS
	) {
		return null;
	}

	const setupSeat = isSetupSeat(input.user.membership.role);
	const asking = setupSeat && !input.organization.hasTrackingReadyProperty;
	const base = `${siteBase(input.baseUrl)}/app/${input.organization.slug ?? input.organization.id}/properties/${input.property.id}`;

	let candidate: SetupEmailPlan | null = null;

	for (const step of input.evaluation.steps) {
		if (step.hidden || step.skipped) {
			continue;
		}
		const route = routeFor(step.id, input.property.subjectType);
		if (!route) {
			continue;
		}
		if (step.status === "done") {
			// Somebody else finished it before this person arrived: worth a
			// short tour, once. Anything they finished themselves, or already
			// had explained to them, needs no email.
			if (
				sequence.tourEligible &&
				sequence.doneAtStart.includes(step.id) &&
				!sequence.toursSent.includes(step.id)
			) {
				candidate = {
					stepId: step.id,
					variant: "tour",
					runId: input.setupRunId,
					href: withRun(`${base}${route}`, input.setupRunId),
					key: `${input.property.id}:${step.id}:tour`,
				};
				break;
			}
			continue;
		}
		if (asking) {
			candidate = {
				stepId: step.id,
				variant: "todo",
				runId: input.setupRunId,
				href: withRun(`${base}${route}`, input.setupRunId),
				key: `${input.property.id}:${step.id}:todo`,
			};
			break;
		}
	}

	// Nothing left to show or ask for. Someone who was never asked to set
	// anything up gets one email about what they can do with the result.
	if (!candidate && !setupSeat && !sequence.closingSentAt) {
		candidate = {
			stepId: CLOSING_STEP_ID,
			variant: "closing",
			runId: null,
			href: `${base}${closingRouteFor(input.property.subjectType)}`,
			key: `${input.property.id}:${CLOSING_STEP_ID}:closing`,
		};
	}

	if (!candidate) {
		return null;
	}

	// The same ask twice running waits, so a step nobody is moving on does not
	// arrive every morning.
	if (
		candidate.key === input.lastKey &&
		input.lastSentAt &&
		input.now.getTime() - input.lastSentAt.getTime() < REPEAT_GAP_MS
	) {
		return null;
	}

	return candidate;
}
